/*
 * Devine le Pokémon (1re génération) — chargé uniquement par la section
 * cgc-guessr. Aucune image pendant la partie : indices comparés à chaque
 * essai ; l'illustration du Pokémon (cgc-guessr-NNN.webp) apparaît sur la
 * carte à la révélation.
 * Pokémon du jour identique pour tous (date de Paris) ; statistiques
 * personnelles dans localStorage (clé cgc-guessr-v1). 10 essais maximum,
 * un joker par partie (dévoile la réponse d'une catégorie non trouvée).
 */
(() => {
  const root = document.querySelector('[data-guessr]');
  if (!root) return;
  // Les traductions Shopify arrivent échappées en HTML (ex. &#39;) : on les décode.
  const decoder = document.createElement('textarea');
  const decode = (value) => {
    if (typeof value === 'string') { decoder.innerHTML = value; return decoder.value; }
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, decode(v)]));
  };
  const t = decode(JSON.parse(document.querySelector('[data-guessr-strings]').textContent));
  const $ = (sel) => root.querySelector(sel);
  const input = $('[data-input]');
  const list = $('[data-list]');
  const submit = $('[data-submit]');
  const rows = $('[data-rows]');
  const results = $('[data-results]');
  const live = $('[data-live]');
  const card = $('[data-card]');
  const win = $('[data-win]');
  const STORE = 'cgc-guessr-v1';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let pokemon = [];
  let byId = {};
  let mode = 'daily';
  let target = null;
  let guesses = [];
  let done = false;
  let matches = [];
  let active = -1;
  let joker = null;
  const CATEGORY_KEYS = ['type1', 'type2', 'stage', 'color', 'habitat', 'height', 'weight'];

  const fmt = (s, values) => s.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => values[k] ?? '');
  const norm = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const lang = document.documentElement.lang || 'fr';
  const num = (n) => n.toLocaleString(lang);

  // Date du jour à Paris (AAAA-MM-JJ) et numéro de jour.
  const parisDate = (d = new Date()) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  const dayNumber = (iso) => Math.floor(Date.parse(`${iso}T00:00:00Z`) / 86400000);

  // Ordre de tirage fixe (mélange déterministe) : pas deux jours de suite dans l'ordre du Pokédex.
  const order = (count) => {
    let seed = 0x43474321;
    const rand = () => {
      seed = (seed + 0x6d2b79f5) | 0;
      let x = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      x = (x + Math.imul(x ^ (x >>> 7), 61 | x)) ^ x;
      return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
    };
    const a = Array.from({ length: count }, (_, i) => i);
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  const load = () => {
    try { return JSON.parse(localStorage.getItem(STORE)) || {}; } catch (error) { return {}; }
  };
  const save = (data) => {
    try { localStorage.setItem(STORE, JSON.stringify(data)); } catch (error) { /* stockage indisponible : le jeu reste jouable */ }
  };
  const stats = () => {
    const data = load();
    data.stats = { played: 0, wins: 0, streak: 0, best: 0, total: 0, lastWin: null, lastPlayed: null, ...(data.stats || {}) };
    return data;
  };

  // ---------- Comparaison ----------
  const compare = (g, x) => {
    const typeCell = (i) => {
      const value = g.types[i] || t.none;
      const goal = x.types[i] || t.none;
      if (value === goal) return { value, state: 'ok' };
      if (g.types[i] && x.types.includes(g.types[i])) return { value, state: 'partial' };
      return { value, state: 'ko' };
    };
    const numCell = (a, b, unit) => ({
      value: `${num(a)} ${unit}`,
      state: a === b ? 'ok' : 'ko',
      arrow: a === b ? '' : (b > a ? 'up' : 'down'),
    });
    return [
      typeCell(0),
      typeCell(1),
      { ...numCell(g.stage, x.stage, ''), value: fmt(t.stage, { stage: g.stage }) },
      { value: g.color, state: g.color === x.color ? 'ok' : 'ko' },
      { value: g.habitat, state: g.habitat === x.habitat ? 'ok' : 'ko' },
      numCell(g.height, x.height, 'm'),
      numCell(g.weight, x.weight, 'kg'),
    ];
  };

  const label = (cell) => {
    const state = cell.state === 'ok' ? t.ok : cell.state === 'partial' ? t.partial : t.ko;
    const arrow = cell.arrow === 'up' ? t.higher : cell.arrow === 'down' ? t.lower : '';
    return [state, arrow].filter(Boolean).join(', ');
  };

  const renderRow = (g, animate) => {
    const tr = document.createElement('tr');
    if (g.id === target.id) tr.classList.add('is-found');
    const th = document.createElement('th');
    th.scope = 'row';
    th.textContent = g.name;
    tr.append(th);
    compare(g, target).forEach((cell, i) => {
      const td = document.createElement('td');
      td.dataset.label = t.categories[CATEGORY_KEYS[i]];
      const tile = document.createElement('span');
      tile.className = `guessr__tile guessr__tile--${cell.state}${cell.arrow ? ` guessr__tile--${cell.arrow}` : ''}`;
      if (animate && !reduced) tile.style.animationDelay = `${i * 110}ms`;
      if (animate && !reduced) tile.classList.add('is-new');
      const text = document.createElement('span');
      text.className = 'guessr__tile-text';
      text.textContent = cell.value;
      tile.append(text);
      if (cell.arrow) {
        const arrow = document.createElement('span');
        arrow.className = 'guessr__tile-arrow';
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = cell.arrow === 'up' ? '↑' : '↓';
        tile.append(arrow);
      }
      const sr = document.createElement('span');
      sr.className = 'visually-hidden';
      sr.textContent = ` (${label(cell)})`;
      tile.append(sr);
      td.append(tile);
      tr.append(td);
    });
    rows.prepend(tr);
  };

  // ---------- Suggestions ----------
  const closeList = () => {
    list.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    active = -1;
  };
  const setActive = (i) => {
    const options = [...list.querySelectorAll('[role="option"]')];
    options.forEach((o, n) => o.setAttribute('aria-selected', String(n === i)));
    active = i;
    if (options[i]) {
      input.setAttribute('aria-activedescendant', options[i].id);
      options[i].scrollIntoView({ block: 'nearest' });
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  };
  const suggest = () => {
    const q = norm(input.value);
    list.replaceChildren();
    if (!q) { closeList(); return; }
    const tried = new Set(guesses);
    matches = pokemon.filter((p) => !tried.has(p.id) && p.key.startsWith(q)).sort((a, b) => a.name.localeCompare(b.name, lang));
    if (!matches.length) {
      const li = document.createElement('li');
      li.className = 'guessr__empty';
      li.textContent = t.no_match;
      list.append(li);
    }
    matches.forEach((p, i) => {
      const li = document.createElement('li');
      li.id = `${list.id}-${p.id}`;
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      li.textContent = p.name;
      li.addEventListener('mousedown', (event) => event.preventDefault());
      li.addEventListener('click', () => guess(p));
      list.append(li);
    });
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    setActive(matches.length ? 0 : -1);
  };

  // ---------- Partie ----------
  const MAX = 10;
  const CATEGORIES = ['type1', 'type2', 'stage', 'color', 'habitat', 'height', 'weight'];

  const updateAttempts = () => {
    const n = guesses.length;
    $('[data-attempts]').textContent = fmt(t.attempts, { count: n, max: MAX });
    const left = MAX - n;
    $('[data-attempts]').classList.toggle('is-low', !done && left <= 3);
  };

  // Joker : dévoile la bonne réponse d'une catégorie pas encore trouvée.
  const jokerValue = (i) => {
    const x = target;
    return [x.types[0], x.types[1] || t.none, fmt(t.stage, { stage: x.stage }), x.color, x.habitat, `${num(x.height)} m`, `${num(x.weight)} kg`][i];
  };
  const renderJoker = () => {
    const button = $('[data-joker]');
    const out = $('[data-joker-clue]');
    button.disabled = done || joker !== null;
    button.textContent = joker === null ? t.joker : t.joker_used;
    if (joker === null) { out.hidden = true; return; }
    out.hidden = false;
    out.textContent = fmt(t.joker_clue, { category: t.categories[CATEGORIES[joker]], value: jokerValue(joker) });
  };
  const useJoker = () => {
    if (done || joker !== null) return;
    const found = new Set();
    guesses.forEach((id) => compare(byId[id], target).forEach((c, i) => { if (c.state === 'ok') found.add(i); }));
    const open = CATEGORIES.map((_, i) => i).filter((i) => !found.has(i));
    // « Type 2 : Aucun » n'aide guère : on le garde seulement s'il ne reste rien d'autre.
    const useful = open.filter((i) => !(i === 1 && !target.types[1]));
    const pool = useful.length ? useful : open.length ? open : CATEGORIES.map((_, i) => i);
    joker = pool[Math.floor(Math.random() * pool.length)];
    persist();
    renderJoker();
    live.textContent = $('[data-joker-clue]').textContent;
  };

  const persist = () => {
    if (mode !== 'daily') return;
    const data = load();
    data.daily = { date: parisDate(), guesses, joker, over: done };
    save(data);
  };

  const reveal = (instant) => {
    $('[data-reveal-number]').textContent = `N° ${String(target.id).padStart(3, '0')}`;
    $('[data-reveal-name]').textContent = target.name;
    $('[data-reveal-types]').textContent = target.types.join(' · ');
    const art = $('[data-reveal-art]');
    art.classList.remove('is-loaded');
    art.onload = () => art.classList.add('is-loaded');
    art.src = root.dataset.art.split('?')[0].replace('001.webp', `${String(target.id).padStart(3, '0')}.webp`);
    card.classList.toggle('is-instant', !!instant);
    card.classList.add('is-revealed');
  };

  const countdown = () => {
    if (mode !== 'daily' || !done) { $('[data-next]').textContent = ''; return; }
    const now = new Date();
    const paris = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Paris' }));
    const left = 24 * 3600 - (paris.getHours() * 3600 + paris.getMinutes() * 60 + paris.getSeconds());
    const h = Math.floor(left / 3600);
    const m = Math.floor((left % 3600) / 60);
    $('[data-next]').textContent = fmt(t.next, { time: `${h} h ${String(m).padStart(2, '0')}` });
  };

  const won = () => guesses.includes(target.id);

  const finish = (instant) => {
    done = true;
    input.disabled = true;
    submit.disabled = true;
    closeList();
    reveal(instant);
    renderJoker();
    updateAttempts();
    const n = guesses.length;
    const key = won() ? (mode === 'daily' ? 'win_daily' : 'win_free') : 'lose';
    $('[data-win-title]').textContent = fmt(t[key], { name: target.name, count: n });
    win.classList.toggle('is-lost', !won());
    win.hidden = false;
    countdown();
  };

  // Statistiques du Pokémon du jour, une seule fois par jour.
  const record = (victory) => {
    const data = stats();
    const today = parisDate();
    const s = data.stats;
    if (s.lastPlayed === today) return;
    const yesterday = parisDate(new Date(Date.now() - 86400000));
    s.played += 1;
    s.lastPlayed = today;
    if (victory) {
      s.wins += 1;
      s.total += guesses.length;
      s.streak = s.lastWin === yesterday ? s.streak + 1 : 1;
      s.best = Math.max(s.best, s.streak);
      s.lastWin = today;
    } else {
      s.streak = 0;
    }
    save(data);
    renderStats();
  };

  const guess = (p) => {
    if (done || !p || guesses.includes(p.id)) {
      if (p && guesses.includes(p.id)) live.textContent = fmt(t.already, { name: p.name });
      return;
    }
    guesses.push(p.id);
    results.hidden = false;
    renderRow(p, true);
    input.value = '';
    closeList();
    const correct = compare(p, target).filter((c) => c.state === 'ok').length;
    live.textContent = fmt(t.live_guess, { name: p.name, count: correct });
    const over = p.id === target.id || guesses.length >= MAX;
    if (over) {
      // Partie terminée tout de suite (plus aucun essai accepté), révélation après l'animation.
      done = true;
      input.disabled = true;
      submit.disabled = true;
      if (mode === 'daily') record(p.id === target.id);
    }
    persist();
    updateAttempts();
    renderJoker();
    if (over) window.setTimeout(() => finish(false), reduced ? 0 : 900);
    else input.focus();
  };

  const start = (nextMode) => {
    mode = nextMode;
    root.querySelectorAll('[data-mode]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.mode === mode)));
    $('[data-question]').textContent = mode === 'daily' ? t.question_daily : t.question_free;
    rows.replaceChildren();
    card.classList.remove('is-revealed', 'is-instant');
    win.hidden = true;
    done = false;
    guesses = [];
    joker = null;
    if (mode === 'daily') {
      const today = parisDate();
      const seq = order(pokemon.length);
      target = pokemon[seq[dayNumber(today) % pokemon.length]];
      const saved = load().daily;
      if (saved && saved.date === today) {
        guesses = saved.guesses.filter((id) => byId[id]).slice(0, MAX);
        joker = Number.isInteger(saved.joker) ? saved.joker : null;
      }
    } else {
      let next;
      do { next = pokemon[Math.floor(Math.random() * pokemon.length)]; } while (pokemon.length > 1 && next === target);
      target = next;
    }
    results.hidden = guesses.length === 0;
    guesses.forEach((id) => renderRow(byId[id], false));
    input.disabled = false;
    submit.disabled = false;
    updateAttempts();
    renderJoker();
    if (won() || guesses.length >= MAX) finish(true);
  };

  // ---------- Statistiques ----------
  const renderStats = () => {
    const s = stats().stats;
    const today = parisDate();
    const yesterday = parisDate(new Date(Date.now() - 86400000));
    const streak = s.lastWin === today || s.lastWin === yesterday ? s.streak : 0;
    const rate = s.played ? `${Math.round((s.wins / s.played) * 100)} %` : '–';
    const average = s.wins ? (s.total / s.wins).toLocaleString(lang, { maximumFractionDigits: 1 }) : '–';
    const items = [[t.stats.played, s.played], [t.stats.wins, rate], [t.stats.streak, streak], [t.stats.best, s.best], [t.stats.average, average]];
    const dl = $('[data-stats]');
    dl.replaceChildren(...items.map(([k, v]) => {
      const div = document.createElement('div');
      const dt = document.createElement('dt');
      const dd = document.createElement('dd');
      dt.textContent = k;
      dd.textContent = v;
      div.append(dd, dt);
      return div;
    }));
  };

  // ---------- Partage ----------
  // Image de partage « CGC Guessr » (aucun spoiler : ni nom, ni indices).
  const loadImage = (src) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
  const drawCard = async ({ width, height, score, date, jokerUsed, tagline }) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    const css = getComputedStyle(document.documentElement);
    const heading = css.getPropertyValue('--font-heading--family').trim() || 'sans-serif';
    const body = css.getPropertyValue('--font-primary--family').trim() || 'sans-serif';
    const yellow = css.getPropertyValue('--color-yellow').trim() || '#FFDE59';
    const orange = css.getPropertyValue('--color-orange').trim() || '#FF914D';
    const blue = css.getPropertyValue('--color-blue').trim() || '#004AAD';
    const purple = css.getPropertyValue('--color-purple').trim() || '#CB6CE6';
    try { await Promise.all([document.fonts.load(`800 80px ${heading}`), document.fonts.load(`700 40px ${body}`)]); } catch (error) { /* polices système */ }
    const unit = Math.min(width, height) / 100;
    const wide = width > height * 1.3;

    // Fond nuit, halo et rayons.
    const bg = ctx.createLinearGradient(0, 0, width, height);
    bg.addColorStop(0, '#1f1844');
    bg.addColorStop(0.55, '#241a4f');
    bg.addColorStop(1, '#3a2468');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, width, height);
    const cx = wide ? width * 0.73 : width / 2;
    const cy = wide ? height * 0.5 : height * 0.555;
    ctx.save();
    ctx.translate(cx, cy);
    for (let i = 0; i < 24; i++) {
      ctx.rotate(Math.PI / 12);
      ctx.fillStyle = i % 2 ? 'rgba(255,222,89,0.05)' : 'rgba(203,108,230,0.05)';
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(width, -width * 0.13);
      ctx.lineTo(width, width * 0.13);
      ctx.fill();
    }
    ctx.restore();
    const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, unit * 48);
    glow.addColorStop(0, 'rgba(203,108,230,0.45)');
    glow.addColorStop(1, 'rgba(203,108,230,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, width, height);

    // Carte mystère « ? ».
    const cardH = unit * (wide ? 70 : 36);
    const cardW = cardH * 63 / 88;
    const roundRect = (x, y, w, h, r) => { ctx.beginPath(); ctx.roundRect(x, y, w, h, r); };
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(0.09);
    ctx.fillStyle = 'rgba(255,255,255,0.18)';
    roundRect(-cardW / 2 + unit * 3, -cardH / 2 - unit * 1.5, cardW, cardH, unit * 3);
    ctx.fill();
    ctx.rotate(-0.17);
    const face = ctx.createLinearGradient(-cardW / 2, -cardH / 2, cardW / 2, cardH / 2);
    face.addColorStop(0, blue);
    face.addColorStop(1, purple);
    ctx.shadowColor = 'rgba(0,0,0,0.45)';
    ctx.shadowBlur = unit * 5;
    ctx.shadowOffsetY = unit * 2;
    ctx.fillStyle = face;
    roundRect(-cardW / 2, -cardH / 2, cardW, cardH, unit * 3);
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.lineWidth = unit * 1.1;
    ctx.strokeStyle = yellow;
    ctx.stroke();
    const mark = ctx.createLinearGradient(0, -cardH * 0.25, 0, cardH * 0.25);
    mark.addColorStop(0, yellow);
    mark.addColorStop(1, orange);
    ctx.fillStyle = mark;
    ctx.font = `800 ${cardH * 0.6}px ${heading}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('?', 0, cardH * 0.04);
    ctx.restore();

    // Logo : badge CGC + « GUESSR ».
    const logo = await loadImage(root.dataset.logo);
    const left = wide ? unit * 9 : width / 2;
    const top = wide ? unit * 14 : unit * 8;
    ctx.textAlign = wide ? 'left' : 'center';
    ctx.textBaseline = 'alphabetic';
    let logoBottom = top;
    if (logo) {
      const lh = unit * (wide ? 13 : 9);
      const lw = lh * logo.width / logo.height;
      ctx.drawImage(logo, wide ? left : left - lw / 2, top, lw, lh);
      logoBottom = top + lh;
    }
    const titleSize = unit * (wide ? 15 : 11);
    const title = ctx.createLinearGradient(0, logoBottom, 0, logoBottom + titleSize);
    title.addColorStop(0, yellow);
    title.addColorStop(1, orange);
    ctx.fillStyle = title;
    ctx.font = `800 ${titleSize}px ${heading}`;
    ctx.fillText('GUESSR', left, logoBottom + titleSize * 1.05);

    // Texte bas : date, score, joker, ou accroche pour l'aperçu du lien.
    ctx.fillStyle = '#ffffff';
    if (tagline) {
      ctx.font = `700 ${unit * 5.6}px ${body}`;
      tagline.forEach((line, i) => ctx.fillText(line, left, logoBottom + titleSize * 1.05 + unit * (10 + i * 7.5)));
    } else {
      const baseY = wide ? height - unit * 16 : height - unit * 13;
      ctx.font = `800 ${unit * (wide ? 14 : 12)}px ${heading}`;
      ctx.fillText(score, left, baseY);
      ctx.font = `700 ${unit * 4.2}px ${body}`;
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.fillText(`${date}${jokerUsed ? ` · ${t.joker_used.replace('🃏 ', '')}` : ''}`, left, baseY + unit * 6.5);
    }

    // Liseré de marque en bas.
    const line = ctx.createLinearGradient(0, 0, width, 0);
    line.addColorStop(0, blue);
    line.addColorStop(0.55, purple);
    line.addColorStop(0.8, yellow);
    line.addColorStop(1, orange);
    ctx.fillStyle = line;
    ctx.fillRect(0, height - unit * 1.4, width, unit * 1.4);
    return canvas;
  };
  window.cgcGuessrCard = drawCard;

  const share = async () => {
    const date = mode === 'daily' ? parisDate().split('-').reverse().join('/') : t.free_label;
    const score = won() ? `${guesses.length}/${MAX}` : `X/${MAX}`;
    const head = fmt(t.share_title, { date, score });
    const url = `${window.location.origin}${window.location.pathname}`;
    const text = `${head}${joker !== null ? ' 🃏' : ''}\n${url}`;
    const button = $('[data-share]');
    const flash = (message) => {
      live.textContent = message;
      const original = button.textContent;
      button.textContent = message;
      window.setTimeout(() => { button.textContent = original; }, 2000);
    };
    try {
      if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
        let files = [];
        try {
          const canvas = await drawCard({ width: 1080, height: 1080, score, date, jokerUsed: joker !== null });
          const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
          if (blob) files = [new File([blob], 'cgc-guessr.png', { type: 'image/png' })];
        } catch (error) { files = []; }
        if (files.length && navigator.canShare && navigator.canShare({ files })) await navigator.share({ files, text });
        else await navigator.share({ text });
      } else {
        await navigator.clipboard.writeText(text);
        flash(t.copied);
      }
    } catch (error) { /* partage annulé */ }
  };

  // ---------- Événements ----------
  input.addEventListener('input', suggest);
  input.addEventListener('focus', () => { if (input.value) suggest(); });
  input.addEventListener('blur', () => window.setTimeout(closeList, 120));
  input.addEventListener('keydown', (event) => {
    const options = list.querySelectorAll('[role="option"]');
    if (event.key === 'ArrowDown' && options.length) {
      event.preventDefault();
      if (list.hidden) suggest();
      setActive((active + 1) % options.length);
    } else if (event.key === 'ArrowUp' && options.length) {
      event.preventDefault();
      setActive((active - 1 + options.length) % options.length);
    } else if (event.key === 'Escape') {
      closeList();
    }
  });
  $('[data-form]').addEventListener('submit', (event) => {
    event.preventDefault();
    if (!list.hidden && active >= 0 && matches[active]) { guess(matches[active]); return; }
    const q = norm(input.value);
    const exact = pokemon.find((p) => p.key === q);
    if (exact) guess(exact);
    else if (q) live.textContent = t.unknown;
  });
  root.querySelectorAll('[data-mode]').forEach((b) => b.addEventListener('click', () => { if (b.dataset.mode !== mode || b.dataset.mode === 'free') start(b.dataset.mode); }));
  $('[data-replay]').addEventListener('click', () => { start('free'); input.focus(); });
  $('[data-share]').addEventListener('click', share);
  $('[data-joker]').addEventListener('click', useJoker);
  window.setInterval(countdown, 30000);

  fetch(root.dataset.src)
    .then((r) => r.json())
    .then((data) => {
      pokemon = data.map((p) => ({ ...p, key: norm(p.name) }));
      byId = Object.fromEntries(pokemon.map((p) => [p.id, p]));
      renderStats();
      start('daily');
    })
    .catch(() => { live.textContent = t.unknown; });
})();
